import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Input, Divider, Checkbox, Radio, message, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import '../components/Product.css';
import { getCurrencySymbol } from '../utils/currencyHelper';
import axios from 'axios';
import config from '../config';

const { TextArea } = Input;

const Product = ({ item, addToCart, showSlug, currencyCode }) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [remarks, setRemarks] = useState('');
    const [selectedAddOns, setSelectedAddOns] = useState([]);
    const [allMenuItems, setAllMenuItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isCustomerContext, setIsCustomerContext] = useState(false);

    const hasSubMenu = Array.isArray(item.subMenu) && item.subMenu.length > 0;
    const hasDescription = !!item.description && item.description.trim() !== '';

    // Fetch all menu items for combo selection (only when needed)
    const fetchAllMenuItems = async () => {
        if (loading || allMenuItems.length > 0) return; // Don't fetch if already loaded

        setLoading(true);
        try {
            const response = await axios.get(`${config.REACT_APP_API_BASE_URL}/api/products/get-product`);
            setAllMenuItems(response.data);
        } catch (error) {
            console.error('Error fetching menu items:', error);
            setAllMenuItems([]); // Set empty array on error
        } finally {
            setLoading(false);
        }
    };

    // Filter menu items by category
    const getItemsByCategory = (categoryName) => {

        const filtered = allMenuItems.filter(menuItem => {
            if (!menuItem.category) return false;

            // More flexible matching - check if category contains the group name or vice versa
            const menuCategory = menuItem.category.toLowerCase();
            const groupName = categoryName.toLowerCase();

            return menuCategory === groupName ||
                menuCategory.includes(groupName) ||
                groupName.includes(menuCategory);
        });

        return filtered;
    };

    const resetModal = () => {
        setRemarks('');
        setSelectedAddOns([]);
    };

    // Fetch menu items only when customer actually opens the modal
    // useEffect(() => {
    //     // console.log('useEffect triggered:', {
    //     //     modalVisible,
    //     //     hasSubMenu,
    //     //     itemCategory: item.category,
    //     //     allMenuItemsLength: allMenuItems.length,
    //     //     loading,
    //     //     isCustomerContext
    //     // });

    //     // For any item with sub-menu, fetch menu items when modal opens
    //     if (modalVisible && hasSubMenu && allMenuItems.length === 0 && !loading) {
    //         fetchAllMenuItems();
    //     }
    // }, [modalVisible]);

    useEffect(() => {
        if (modalVisible && hasSubMenu && allMenuItems.length === 0 && !loading) {
            fetchAllMenuItems();
        }

        if (modalVisible && hasSubMenu) {
            const compulsoryAddOns = [];

            item.subMenu?.forEach(group => {
                if (group.show === false) return;

                if (group.selection === "compulsory" && group.items) {
                    group.items.forEach(menuItem => {
                        compulsoryAddOns.push({
                            group: group.group,
                            name: menuItem.name,
                            price: menuItem.price || 0,
                            id: menuItem.id || menuItem._id,
                            pid: menuItem.pid,
                            _id: menuItem._id,
                            isCategoryItem: !!menuItem._id,
                            customUnit: menuItem.customUnit || null,
                            printer: group.printer || [], // Added printer from group
                        });
                    });
                }
            });


            if (compulsoryAddOns.length > 0) {
                setSelectedAddOns(prev => {
                    const filtered = prev.filter(a =>
                        !compulsoryAddOns.some(comp => comp.group === a.group)
                    );
                    return [...filtered, ...compulsoryAddOns];
                });
            }
        }
    }, [modalVisible, hasSubMenu, allMenuItems.length, loading, item.subMenu]);

    const handleCardClick = () => {
        if (hasSubMenu) {
            setIsCustomerContext(true); // Mark as customer interaction
            resetModal();
            setModalVisible(true);
        } else {
            addToCart({ ...item, addOns: selectedAddOns, remarks, cartKey: `${item._id || item.id}-${Date.now()}-${Math.random()}` });
        }
    };

    const handleAddClick = (e) => {
        e.stopPropagation();
        setIsCustomerContext(true); // Mark as customer interaction
        resetModal();
        setModalVisible(true);
    };

    // 单选
    const handleRadioChange = (group, selectedId) => {

        // All items are now stored in group.items
        const selectedItem = group.items.find(i => (i.id || i._id) === selectedId);

        setSelectedAddOns(prev => {
            const filtered = prev.filter(a => a.group !== group.group);
            const newAddOns = selectedItem
                ? [...filtered, {
                    group: group.group,
                    name: selectedItem.name,
                    price: selectedItem.price || 0,
                    id: selectedItem.id || selectedItem._id,
                    pid: selectedItem.pid,
                    _id: selectedItem._id,
                    isCategoryItem: !!selectedItem._id,
                    customUnit: selectedItem.customUnit || null,
                    printer: group.printer || [], // Added printer from group
                }]
                : filtered;
            return newAddOns;
        });
    };

    // 多选
    const handleCheckboxChange = (group, checkedIds) => {
        // All items are now stored in group.items
        const checkedItems = group.items.filter(i => checkedIds.includes(i.id || i._id));

        setSelectedAddOns(prev => {
            const filtered = prev.filter(a => a.group !== group.group);
            const newAddOns = checkedItems.map(addOnItem => ({
                group: group.group,
                name: addOnItem.name,
                price: addOnItem.price || 0,
                id: addOnItem.id || addOnItem._id,
                pid: addOnItem.pid,
                _id: addOnItem._id,
                isCategoryItem: !!addOnItem._id,
                customUnit: addOnItem.customUnit || null,
                printer: group.printer || [], // Added printer from group
            }));
            return [...filtered, ...newAddOns];
        });
    };

    const handleModalOk = () => {
        if (hasSubMenu) {
            for (const group of item.subMenu) {
                if (
                    group.requirement === "required" &&
                    !selectedAddOns.some(a => a.group === group.group)
                ) {
                    message.error(`Please select at least one option from ${group.group}.`);
                    return;
                }
            }
        }
        const itemWithAddOns = {
            ...item,
            addOns: selectedAddOns,
            remarks,
            cartKey: `${item._id || item.id}-${Date.now()}-${Math.random()}`
        };
        addToCart(itemWithAddOns);
        setModalVisible(false);
        resetModal();
    };

    const handleModalCancel = () => {
        setModalVisible(false);
        resetModal();
    };

    const getSelectedIds = (group) => {
        return selectedAddOns
            .filter(a => a.group === group.group)
            .map(a => a.id);
    };

    // outofstock - only consider out of stock if quantity is explicitly set to 0
    // If quantity is null/undefined/empty, treat as unlimited stock
    const est = item.quantity === null || item.quantity === undefined
        ? 'NIL'
        : Math.floor(item.quantity / (item.quantityUnit || 1));

    const isOutOfStock = est === 0;

    // Check if any add-ons are out of stock
    const checkAddOnStock = (group) => {
        if (!group.items) return false;
        return group.items.some(addOnItem => {
            // For category items, we need to check the actual product stock
            // For custom options, they don't have stock limits
            if (addOnItem._id) {
                // This is a category item, check if it has stock
                return addOnItem.quantity === 0;
            }
            return false; // Custom options don't have stock limits
        });
    };

    return (
        <>
            <Card
                hoverable
                className="product-card"
                bodyStyle={{ padding: 16, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
                onClick={handleCardClick}
                style={{
                    pointerEvents: isOutOfStock ? 'none' : 'auto',
                    position: 'relative',
                }}
            >
                {/* outofstock */}
                {isOutOfStock && (
                    <div
                        className='outofstock-label'
                        style={{
                            position: 'absolute',
                            bottom: 10,
                            right: 10,
                            background: '#ff4d4f',
                            color: '#fff',
                            padding: '4px 8px',
                            borderRadius: 4,
                            fontWeight: 600,
                            fontSize: 15,
                            zIndex: 2,
                        }}
                    >
                        Out of Stock
                    </div>
                )}

                {/* unavailable */}
                {item.status === false && (
                    <div
                        style={{
                            position: 'absolute',
                            left: 10,
                            bottom: 2,
                            color: '#1890ff',
                            background: 'rgba(255,255,255,0.85)',
                            fontWeight: 600,
                            fontSize: 14,
                            zIndex: 2,
                            borderRadius: 4,
                            padding: '2px 8px'
                        }}
                    >
                        Unavailable
                    </div>
                )}

                <div style={{ filter: isOutOfStock ? 'grayscale(90%) opacity(0.2)' : 'none' }}>
                    <div>
                        <div className="product-name">{showSlug ? item.slug : item.name}</div>
                        <div className="product-price">
                            {getCurrencySymbol(currencyCode)} {item.price.toFixed(2)}
                        </div>
                    </div>
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                        <Button
                            type="primary"
                            shape="circle"
                            icon={<PlusOutlined />}
                            className="add-to-cart-button"
                            onClick={handleAddClick}
                            disabled={isOutOfStock}
                        />
                    </div>
                </div>
            </Card>

            <Modal
                title={item.name}
                open={modalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okText="Add to Cart"
                cancelText="Cancel"
            >
                {hasDescription && (
                    <div style={{ marginBottom: 16, color: '#666' }}>
                        {item.description}
                    </div>
                )}

                {hasSubMenu && item.subMenu
                    ?.slice()
                    .sort((a, b) => {
                        // Move "compulsory" groups to the top
                        if (a.selection === "compulsory" && b.selection !== "compulsory") return -1;
                        if (b.selection === "compulsory" && a.selection !== "compulsory") return 1;
                        return 0;
                    })
                    .map(group => {

                        // Use items from the group (no longer filtering by group name)
                        let categoryItems = group.items || [];

                        return (
                            group.show === false ? null : (
                                <div key={group.group} style={{ marginBottom: 16 }}>
                                    <div style={{ fontWeight: 500, marginBottom: 8 }}>
                                        {group.group}
                                        {group.requirement === "required" && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                                    </div>

                                    {group.requirement === "required" && (
                                        <div style={{ color: 'red', fontSize: 13, marginBottom: 6 }}>
                                            {group.selection === "single"
                                                ? `Please select at least one option from ${group.group}.`
                                                : `Please select at least one option from ${group.group}.`
                                            }
                                        </div>
                                    )}

                                    {loading ? (
                                        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                            🔄 Loading menu items from your entire menu...
                                        </div>
                                    ) : categoryItems.length === 0 ? (
                                        <div style={{ color: '#999', fontStyle: 'italic' }}>
                                            No items available for {group.group}
                                        </div>
                                    ) : group.selection === "single" ? (
                                        <Radio.Group
                                            onChange={e => handleRadioChange(group, e.target.value)}
                                            value={getSelectedIds(group)[0] || null}
                                        >
                                            {categoryItems.map(menuItem => {
                                                const shouldShowPrice = menuItem.price > 0;
                                                // const isOutOfStock = menuItem.quantity === 0; // Only out of stock if explicitly 0

                                                // const est = menuItem.quantity === null || menuItem.quantity === undefined
                                                //     ? 'NIL'
                                                //     : Math.floor(menuItem.quantity / (menuItem.quantityUnit || 1));

                                                // const isOutOfStock = est === 0;

                                                const getProductStockStatusById = (id, fallbackQuantity) => {
                                                    const foundProduct = allMenuItems.find(p => p._id === id || p.id === id);
                                                    const quantity = foundProduct ? foundProduct.quantity : fallbackQuantity;
                                                    const est = quantity === null || quantity === undefined
                                                        ? 'NIL'
                                                        : Math.floor(quantity / (foundProduct?.quantityUnit || 1));
                                                    return est === 0;
                                                };
                                                const isOutOfStock = getProductStockStatusById(menuItem.id || menuItem._id, menuItem.quantity);

                                                // const getProductNameById = (id, fallbackName) => {
                                                //     const foundProduct = allMenuItems.find(
                                                //         p => p._id === id || p.id === id
                                                //     );
                                                //     return foundProduct ? foundProduct.name : fallbackName;
                                                // };

                                                // const displayName = getProductNameById(menuItem.id || menuItem._id, menuItem.name);

                                                return (
                                                    <Radio
                                                        key={menuItem.id || menuItem._id}
                                                        value={menuItem.id || menuItem._id}
                                                        disabled={isOutOfStock}
                                                        style={{
                                                            color: isOutOfStock ? '#999' : 'inherit',
                                                            opacity: isOutOfStock ? 0.6 : 1
                                                        }}
                                                    >
                                                        {menuItem.name} {shouldShowPrice ? `(+${currencyCode} ${menuItem.price.toFixed(2)})` : ''}
                                                        {isOutOfStock}
                                                    </Radio>
                                                );
                                            })}
                                        </Radio.Group>

                                    ) : group.selection === "compulsory" ? (
                                        <Checkbox.Group
                                            value={categoryItems.map(item => item.id || item._id)} // auto-select all
                                            options={categoryItems.map(menuItem => {
                                                const shouldShowPrice = menuItem.price > 0;

                                                return {
                                                    label: (
                                                        <span style={{ color: '#999', opacity: 0.7 }}>
                                                            {menuItem.name} {shouldShowPrice ? `(+${currencyCode} ${menuItem.price.toFixed(2)})` : ''}
                                                        </span>
                                                    ),
                                                    value: menuItem.id || menuItem._id,
                                                    disabled: true, // disable selection
                                                };
                                            })}
                                        />
                                    ) : (
                                        <Checkbox.Group
                                            options={categoryItems.map(menuItem => {
                                                const shouldShowPrice = menuItem.price > 0;
                                                // const isOutOfStock = menuItem.quantity === 0; // Only out of stock if explicitly 0
                                                // const est = menuItem.quantity === null || menuItem.quantity === undefined
                                                //     ? 'NIL'
                                                //     : Math.floor(menuItem.quantity / (menuItem.quantityUnit || 1));

                                                // const isOutOfStock = est === 0;

                                                const getProductStockStatusById = (id, fallbackQuantity) => {
                                                    const foundProduct = allMenuItems.find(p => p._id === id || p.id === id);
                                                    const quantity = foundProduct ? foundProduct.quantity : fallbackQuantity;
                                                    const est = quantity === null || quantity === undefined
                                                        ? 'NIL'
                                                        : Math.floor(quantity / (foundProduct?.quantityUnit || 1));
                                                    return est === 0;
                                                };
                                                const isOutOfStock = getProductStockStatusById(menuItem.id || menuItem._id, menuItem.quantity);

                                                // const getProductNameById = (id, fallbackName) => {
                                                //     const foundProduct = allMenuItems.find(
                                                //         p => p._id === id || p.id === id
                                                //     );
                                                //     return foundProduct ? foundProduct.name : fallbackName;
                                                // };

                                                // const displayName = getProductNameById(menuItem.id || menuItem._id, menuItem.name);

                                                return {
                                                    label: (
                                                        <span style={{
                                                            color: isOutOfStock ? '#999' : 'inherit',
                                                            opacity: isOutOfStock ? 0.6 : 1
                                                        }}>
                                                            {menuItem.name} {shouldShowPrice ? `(+${currencyCode} ${menuItem.price.toFixed(2)})` : ''}
                                                            {isOutOfStock}
                                                        </span>
                                                    ),
                                                    value: menuItem.id || menuItem._id,
                                                    disabled: isOutOfStock
                                                };
                                            })}
                                            value={getSelectedIds(group)}
                                            onChange={checkedIds => handleCheckboxChange(group, checkedIds)}
                                        />
                                    )}
                                </div>
                            )
                        );
                    })}
                <Divider />
                <div>
                    <div style={{ fontWeight: 500, marginBottom: 8 }}>Remarks</div>
                    <TextArea
                        rows={2}
                        placeholder="e.g. Less sugar, no spicy"
                        value={remarks}
                        onChange={e => setRemarks(e.target.value)}
                    />
                </div>
            </Modal>
        </>
    );
};

export default Product;